import styled from "styled-components";
import { color, font, layout, eyebrow } from "../../theme/tokens";

/**
 * The hero is sized by its own padding, not by `min-height: Nvh`. The old
 * version centred a short block inside 78vh, which opened a several-hundred
 * pixel dead zone on tall desktop windows.
 */
export const HeroContainer = styled.section`
  display: flex;
  justify-content: center;
  background: ${color.paper};
  border-bottom: 1px solid ${color.ruleSoft};
  padding: 128px 0 112px;

  @media screen and (max-width: 900px) {
    padding: 88px 0 72px;
  }

  @media screen and (max-width: 600px) {
    padding: 64px 0 56px;
  }
`;

export const HeroContent = styled.div`
  width: 100%;
  max-width: ${layout.maxWidth};
  padding: 0 ${layout.gutter};
  text-align: left;
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  gap: 64px;
  align-items: end;

  @media screen and (max-width: 1000px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }

  @media screen and (max-width: 800px) {
    padding: 0 ${layout.gutterMobile};
  }
`;

export const HeroMain = styled.div`
  min-width: 0;
`;

/** Right-hand fact column — keeps the hero from reading as a half-empty page. */
export const HeroAside = styled.div`
  border-top: 1px solid ${color.rule};
  padding-top: 24px;
`;

export const HeroAsideLabel = styled.p`
  ${eyebrow}
  margin-bottom: 18px;
`;

export const HeroAsideList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;

  li {
    font-family: ${font.body};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${color.ink};
    border: 1px solid ${color.rule};
    border-radius: 999px;
    padding: 7px 13px;
    white-space: nowrap;
  }

  /* Seven full-width chips stack into ~500px of scroll on a phone. Tighten
     them so they pack two per row instead of one. */
  @media screen and (max-width: 600px) {
    gap: 6px 8px;

    li {
      font-size: 10.5px;
      letter-spacing: 0.03em;
      padding: 6px 10px;
    }
  }
`;

export const HeroAsideStat = styled.div`
  margin-top: 28px;

  strong {
    display: block;
    font-family: ${font.display};
    font-size: 44px;
    font-weight: 400;
    line-height: 1;
    color: ${color.clay};
  }

  span {
    display: block;
    margin-top: 10px;
    font-family: ${font.body};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${color.inkMuted};
  }
`;

export const HeroEyebrow = styled.p`
  ${eyebrow}
  margin-bottom: 28px;
`;

/**
 * Large and light rather than large and black. Weight 400 at this size reads
 * as considered; weight 900 reads as a landing-page template.
 */
export const HeroH1 = styled.h1`
  font-family: ${font.display};
  color: ${color.green};
  font-size: 76px;
  font-weight: 400;
  letter-spacing: -0.015em;
  line-height: 1.1;
  margin: 0;
  max-width: 15ch;

  @media screen and (max-width: 1100px) {
    font-size: 60px;
  }

  @media screen and (max-width: 800px) {
    font-size: 46px;
    max-width: none;
  }

  @media screen and (max-width: 480px) {
    font-size: 36px;
  }
`;

export const HeroP = styled.p`
  font-family: ${font.body};
  margin: 32px 0 0;
  color: ${color.ink};
  font-size: 19px;
  font-weight: 400;
  line-height: 1.65;
  max-width: 54ch;

  @media screen and (max-width: 800px) {
    font-size: 17px;
    margin-top: 24px;
  }
`;

/** Shared band wrapper so every section sits on the same vertical rhythm. */
export const Band = styled.section`
  display: flex;
  justify-content: center;
  background: ${({ $tone }) =>
    $tone === "deep" ? color.paperDeep : color.paper};
  padding: 104px 0;

  @media screen and (max-width: 900px) {
    padding: 72px 0;
  }
`;

export const BandInner = styled.div`
  width: 100%;
  max-width: ${layout.maxWidth};
  padding: 0 ${layout.gutter};

  @media screen and (max-width: 800px) {
    padding: 0 ${layout.gutterMobile};
  }
`;

export const BandEyebrow = styled.p`
  ${eyebrow}
`;

export const BandHeading = styled.h2`
  font-family: ${font.display};
  color: ${color.inkStrong};
  font-size: 44px;
  font-weight: 400;
  letter-spacing: -0.01em;
  line-height: 1.15;
  margin: 20px 0 0;

  @media screen and (max-width: 800px) {
    font-size: 32px;
  }
`;

export const BandHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 32px;
  padding-bottom: 32px;
  margin-bottom: 48px;
  border-bottom: 1px solid ${color.rule};

  @media screen and (max-width: 800px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 32px;
  }
`;

/** Underlined text link with an arrow — the site's one repeated CTA pattern. */
export const ArrowLink = styled.a`
  display: inline-flex;
  align-items: center;
  /* 44px keeps it a comfortable touch target on a phone. */
  min-height: 44px;
  font-family: ${font.display};
  font-size: 18px;
  font-weight: 500;
  color: ${color.green};
  text-decoration: none;
  white-space: nowrap;
  border-bottom: 1px solid ${color.green};
  padding-bottom: 3px;
  transition: color 160ms ease, border-color 160ms ease;

  &:hover {
    color: ${color.clay};
    border-bottom-color: ${color.clay};
  }

  /* inline-flex collapses a leading space in the pseudo-element, so set the
     gap explicitly. */
  &:after {
    content: "\\2192";
    margin-left: 0.4em;
  }
`;

export const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-family: ${font.body};
  font-size: 16px;

  a {
    /* Padding rather than gap, so the touch target covers the full row. */
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: ${color.white};
    text-decoration: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.4);
    transition: border-color 160ms ease;

    &:hover {
      border-bottom-color: ${color.white};
    }
  }
`;
